import { ENEMIES } from '../../data/enemies';
import { GROUND_Y, SIM_HZ, WORLD_TOP } from '../../data/tuning';
import { createAabb, createCircle, type Aabb, type Circle } from '../collision';

/**
 * EN-21 Grúa portuaria (GDD §8.3, issue #104). Pure and deterministic: everything is computed from the ticks
 * since the crane was first active (no accumulated angle, so no drift), plus `params.phaseOffset` (s) and
 * `params.startBlocked` (0 high, 1 low). The structure is fixed in the world and scrolls by like a column.
 */
export const EN21 = ENEMIES['EN-21'];

export type CranePassage = 'high' | 'low';
export type CraneBlocked = CranePassage | 'rotating';

/** Per-instance parameters of the level data (`SegmentDef.enemies[].params`). */
export interface CraneParams {
  startBlocked?: number;
  phaseOffset?: number;
}

export interface CraneState {
  /** Ticks since it was first active (it keeps counting after it leaves the screen). */
  ticks: number;
}

export interface CraneView {
  blocked: CraneBlocked;
  /** Angle of the arm in degrees: +armAngleDeg points up-right (high), -armAngleDeg down-right (low). */
  angle: number;
  /** The warning beacon: on during the second before every rotation (pillar 2, GDD §2). */
  beacon: boolean;
  /** Seconds into the current period, from 0 (for the rotation timer of the scenarios). */
  timer: number;
}

export const createCrane = (): CraneState => ({ ticks: 0 });

export const stepCrane = (state: CraneState): void => {
  state.ticks += 1;
};

const angleOf = (passage: CranePassage) => (passage === 'high' ? EN21.armAngleDeg : -EN21.armAngleDeg);
const other = (passage: CranePassage): CranePassage => (passage === 'high' ? 'low' : 'high');

/** The crane after `ticks` ticks: rotations start at every multiple of the period and last `rotationSeconds`. */
export function craneView(ticks: number, params: CraneParams = {}): CraneView {
  const start: CranePassage = params.startBlocked === 1 ? 'low' : 'high';
  const period = EN21.periodSeconds;
  const local = ticks / SIM_HZ - (params.phaseOffset ?? 0);
  // Before its phase offset the crane waits in its start position; no rotation is coming yet.
  if (local < 0) return { blocked: start, angle: angleOf(start), beacon: false, timer: 0 };
  const cycle = Math.floor(local / period);
  const within = local - cycle * period;
  const beacon = within >= period - EN21.beaconLeadSeconds;
  if (cycle >= 1 && within < EN21.rotationSeconds) {
    const from = cycle % 2 === 1 ? start : other(start);
    const fraction = within / EN21.rotationSeconds;
    return { blocked: 'rotating', angle: angleOf(from) + (angleOf(other(from)) - angleOf(from)) * fraction, beacon: false, timer: within };
  }
  const blocked = cycle % 2 === 0 ? start : other(start);
  return { blocked, angle: angleOf(blocked), beacon, timer: within };
}

/** The fixed structure around the beam centre (x, y): bottom mast, beam and top mast, as AABBs. */
export function craneStructure(x: number, y: number): Aabb[] {
  const width = EN21.hitboxWidth;
  const beam = EN21.hitboxHeight;
  const lowBottom = y - beam / 2 - EN21.passageHeight;
  const highTop = y + beam / 2 + EN21.passageHeight;
  const pieces = [createAabb(x, y, width, beam)];
  if (lowBottom > GROUND_Y) pieces.push(createAabb(x, (GROUND_Y + lowBottom) / 2, width, lowBottom - GROUND_Y));
  if (highTop < WORLD_TOP) pieces.push(createAabb(x, (highTop + WORLD_TOP) / 2, width, WORLD_TOP - highTop));
  return pieces;
}

/** The arm as a chain of circles from the pivot (the beam centre) along `angle`, spaced by their radius. */
export function armCircles(x: number, y: number, angle: number): Circle[] {
  const radians = (angle * Math.PI) / 180;
  const count = Math.ceil(EN21.armLength / EN21.armRadius);
  return Array.from({ length: count + 1 }, (_, i) => {
    const along = (EN21.armLength * i) / count;
    return createCircle(x + Math.cos(radians) * along, y + Math.sin(radians) * along, EN21.armRadius);
  });
}

/** Centre heights of the two passages of a crane whose beam is at `y`. */
export const passageY = (y: number, passage: CranePassage): number =>
  y + (passage === 'high' ? 1 : -1) * (EN21.hitboxHeight / 2 + EN21.passageHeight / 2);
